import { 
  AlertTriangle, 
  Shield, 
  Clock, 
  Filter,
  Search
} from 'lucide-react';

export function Alerts() {
  // Mock data for alerts
  const alerts = [
    {
      id: '1',
      type: 'info',
      title: 'System Ready',
      message: 'Chat Safe monitoring is active and processing messages.',
      timestamp: '2024-11-08T10:30:00Z',
      read: false,
    },
    {
      id: '2',
      type: 'success',
      title: 'All Clear',
      message: 'No concerning content detected in the last 24 hours.',
      timestamp: '2024-11-08T09:15:00Z',
      read: true,
    },
  ];

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-warning-500" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-error-500" />;
      case 'success':
        return <Shield className="h-5 w-5 text-success-500" />;
      default:
        return <Shield className="h-5 w-5 text-primary-500" />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'warning':
        return 'border-l-warning-500 bg-warning-50';
      case 'error':
        return 'border-l-error-500 bg-error-50';
      case 'success':
        return 'border-l-success-500 bg-success-50';
      default:
        return 'border-l-primary-500 bg-primary-50';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Alerts & Activity</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor system alerts and safety notifications
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search alerts..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            {alerts.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="mx-auto h-16 w-16 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No alerts</h3>
                <p className="mt-2 text-sm text-gray-500">
                  All systems are running normally. You'll see alerts here when they occur.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`border-l-4 p-4 rounded-r-lg ${getAlertColor(alert.type)} ${
                      !alert.read ? 'ring-2 ring-primary-200' : ''
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {getAlertIcon(alert.type)}
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900">
                            {alert.title}
                            {!alert.read && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                New
                              </span>
                            )}
                          </h3>
                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatTime(alert.timestamp)}
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-gray-700">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info Panel */}
        <div className="mt-8 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <div className="flex items-start">
            <Shield className="h-6 w-6 text-primary-600 mt-1" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-primary-900">
                About Chat Safe Monitoring
              </h3>
              <div className="mt-2 text-sm text-primary-700">
                <p>
                  Chat Safe monitors your connected chat platforms for potentially concerning content 
                  while maintaining privacy. The system analyzes conversation patterns and context 
                  without exposing full message content to parents.
                </p>
                <p className="mt-2">
                  <strong>Current Status:</strong> All systems operational. 
                  AI analysis pipeline will be available in Phase 2.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
